import { useMemo } from 'react'

import { MoreOutlined, StarOutlined } from '@ant-design/icons'
import { Button, Dropdown, Menu, Tooltip } from 'antd'
import classNames from 'classnames'
import { getFilesPreviewIsPublicLinkSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { MemberRole } from 'features/team/model/types/members'
import { getUserRole } from 'features/user/model/selectors/userSelectors'
import { useGetEnablePermission } from 'features/public-link-files/hooks/useGetEnablePermission'
import { useAppSelector } from 'shared/config/store'
import { MenuProps } from 'shared/ui'
import { StatusSelector } from 'shared/ui/StatusSelector'
import { hasAccess } from 'shared/utils/access'
import { checkPermission } from 'shared/utils/checkPermission'
import styled from 'styled-components'
import { BoardToolbarAction } from 'shared/constants/toolbar'

import {
  ArrowRightOutlined,
  ArrowsAltOutlined,
  CopyOutlined, DeleteOutlined,
  DownloadOutlined, EditOutlined,
  LinkOutlined, PlusCircleOutlined
} from '../../../shared/ui'

interface BoardStatusOption {
  label: string
  key: string
  color: string
}

interface BoardToolbarTopProps {
  status?: BoardStatusOption | null
  statuses: BoardStatusOption[]
  onClickAction: (actionId: string) => void
  onChangeStatus: (status: BoardStatusOption) => void
  onAddBoard: () => void
  contextMenuItems?: MenuProps['items']
  disabled?: boolean
  withOnlyStatus?: boolean
  withoutUserInteraction?: boolean
}

export const BoardToolbarTop = ({
  status,
  statuses,
  onClickAction,
  onChangeStatus,
  onAddBoard,
  contextMenuItems,
  disabled,
  withOnlyStatus = false,
  withoutUserInteraction = false
}: BoardToolbarTopProps) => {
  const userRole = useAppSelector(getUserRole)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const { isDownloadEnabled, isEditContentEnabled } = useGetEnablePermission()
  
  const menuItems = useMemo(() => {
    if (contextMenuItems) {
      return contextMenuItems
    }

    const defaultContextMenuItems = [
      {
        key: BoardToolbarAction.Open,
        label: 'Open',
        icon: <ArrowsAltOutlined />,
      },
      hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
        key: BoardToolbarAction.Share,
        label: 'Share link',
        icon: <LinkOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        type: 'divider' as const,
      },
      isDownloadEnabled && {
        key: BoardToolbarAction.Download,
        label: 'Download',
        icon: <DownloadOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Copy,
        label: 'Copy to board',
        icon: <CopyOutlined />,
      },
      isEditContentEnabled && {
        key: BoardToolbarAction.Move,
        label: 'Move to board',
        icon: <ArrowRightOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Rename,
        label: 'Rename',
        icon: <EditOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], isEditContentEnabled, isPublicLink) && {
        type: 'divider' as const,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Delete,
        label: 'Delete from library',
        icon: <DeleteOutlined />,
        danger: true,
      }
    ].filter(Boolean)
  
    // const excludeActions = [BoardToolbarAction.Open, BoardToolbarAction.Move, BoardToolbarAction.Delete]
  
    // return defaultContextMenuItems.filter((item) => {
    //   return !excludeActions.includes(item.key as BoardToolbarAction)
    // })
    return defaultContextMenuItems
  }, [contextMenuItems, userRole])

  
  const handleMenuClick: MenuProps['onClick'] = (data) => {
    onClickAction(data.key)
  }

  const menu = (
    <Menu items={menuItems}
      onClick={handleMenuClick}
    />
  )

  return (
    <SToolbar className={classNames({
      'without-user-interaction': withoutUserInteraction,
    })}
    >
      <SLeftSection>

        <StatusSelector
          status={status}
          statuses={statuses}
          onChangeStatus={onChangeStatus}
          disabled={disabled}
          showBorder={true}
        />

        {
          !withOnlyStatus && (
            <>
              { isEditContentEnabled && userRole !== MemberRole.GUEST && (
                <Tooltip title="Create board">
                  <SAddBoardButton onClick={onAddBoard}>
                    <PlusCircleOutlined />
                  </SAddBoardButton>
                </Tooltip>
              )}

              { !isPublicLink && (
                <Tooltip title="Add to favorites">
                  <StarButton>
                    <StarOutlined />
                  </StarButton>
                </Tooltip>
              )}
            </>
          )
        }        
        
      </SLeftSection>

      {
        !withOnlyStatus && (
          <Dropdown overlay={menu}
            trigger={['click']}
          >
            <SMoreButton type="text">
              <MoreOutlined />
            </SMoreButton>
          </Dropdown>
        )
      }
    </SToolbar>
  )
}

const SToolbar = styled.div`
  height: 20px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  width: fit-content;

  &.without-user-interaction {
    pointer-events: none;
    cursor: default;
  }
`

const SLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const StarButton = styled(Button)`
  height: 20px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  border: none;
  box-shadow: none;
  margin-right: 12px;

  &:hover {
    background: transparent;
  }
`

const SAddBoardButton = styled(Button)`
  height: 20px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  border: none;
  box-shadow: none;

  &:hover {
    background: transparent;
  }
`

const SMoreButton = styled(Button)`
  height: 20px;
  padding: 0 4px;
  display: flex;
  align-items: center;
`
